/*
 * AURA — tło shaderowe hero.
 *
 * Port z landinga Astro (repo s0utl44/aura, branch
 * claude/landing-page-structure-style-k0eoc9, katalog web/src/shaders/).
 * GLSL jest przeniesiony bez zmian — jest sprawdzony i zmierzony. Przepisana
 * jest wyłącznie warstwa montażu: React → custom element, konfiguracja
 * z ustawień sekcji zamiast propsów.
 *
 * Rdzeń pochodzi z 21st.dev Shader Builder (wariant „Plasma"). Shaderów nie
 * bierzemy z Shadertoya: domyślna licencja tego serwisu to CC BY-NC-SA, czyli
 * bez użytku komercyjnego, a AURA jest produktem sprzedażowym.
 */
(function () {
  'use strict';

  var VERT = 'attribute vec2 a_position;\nvoid main() {\n  gl_Position = vec4(a_position, 0.0, 1.0);\n}';

  /* Preambuła: uniformy, szum, paleta, konwersje barw. */
  var PREAMBLE = `#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform vec3 u_colors[8];
// Siedem spakowanych wektorów + osiem kolorów = 15 wektorów uniformów
// fragmentu, jeden poniżej gwarantowanego minimum WebGL1. Makra zachowują
// czytelne nazwy u_*.
uniform vec4 u_scene;      // resolution.xy, time, liczba kolorów
uniform vec4 u_shape;      // scale, intensity, paramA, warp
uniform vec4 u_surface;    // detail, contrast, brightness, saturation
uniform vec4 u_finish;     // hue, vignette, blur, grain
uniform vec4 u_transform;  // seed, rotation, drift, przełącznik OKLab
uniform vec4 u_space;      // offset.xy, pointer.xy
uniform vec4 u_cursor;

#define u_resolution u_scene.xy
#define u_time u_scene.z
#define u_colorCount u_scene.w
#define u_scale u_shape.x
#define u_intensity u_shape.y
#define u_paramA u_shape.z
#define u_warp u_shape.w
#define u_detail u_surface.x
#define u_contrast u_surface.y
#define u_brightness u_surface.z
#define u_saturation u_surface.w
#define u_hue u_finish.x
#define u_vignette u_finish.y
#define u_blur u_finish.z
#define u_grain u_finish.w
#ifdef GL_FRAGMENT_PRECISION_HIGH
#define u_seed u_transform.x
#else
// Utrzymuje wejścia hashy w gwarantowanym zakresie mediump (±2^14).
#define u_seed mod(u_transform.x, 31.0)
#endif
#define u_rotate u_transform.y
#define u_drift u_transform.z
#define u_oklab u_transform.w
#define u_offset u_space.xy
#define u_mouse u_space.zw
#define u_cursorPresence u_cursor.x
#define u_cursorEffect u_cursor.y
#define u_cursorStrength u_cursor.z
#define u_cursorRadius u_cursor.w

float hash21(vec2 p) {
#ifndef GL_FRAGMENT_PRECISION_HIGH
  p = mod(p, 31.0);
#endif
  p = fract(p * vec2(234.34, 435.345));
  p += dot(p, p + 34.23);
  return fract(p.x * p.y);
}

// Równomierny szum pod ziarno filmowe. Hash powyżej nadaje się do szumu
// wartościowego, ale na płaskich polach pokazuje siatkę wzdłuż osi.
float grainHash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash21(i), hash21(i + vec2(1.0, 0.0)), u.x),
    mix(hash21(i + vec2(0.0, 1.0)), hash21(i + vec2(1.0, 1.0)), u.x),
    u.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p = p * 2.03 + vec2(17.0, 9.2);
    a *= 0.5;
  }
  return v;
}

// --- Mieszanie kolorów w OKLab (percepcyjne), włączane przez u_oklab --------
vec3 srgbToLinear(vec3 c) {
  return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(0.04045, c));
}
vec3 linearToSrgb(vec3 c) {
  // max() chroni gałąź sRGB: interpolacja w OKLab potrafi wyjść poza gamut
  // i zepchnąć kanał poniżej zera, a pow(ujemna, …) daje NaN.
  return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055,
    step(0.0031308, c));
}
vec3 linToOklab(vec3 c) {
  float l = 0.4122214708 * c.r + 0.5363325363 * c.g + 0.0514459929 * c.b;
  float m = 0.2119034982 * c.r + 0.6806995451 * c.g + 0.1073969566 * c.b;
  float s = 0.0883024619 * c.r + 0.2817188376 * c.g + 0.6299787005 * c.b;
  l = pow(max(l, 0.0), 1.0 / 3.0);
  m = pow(max(m, 0.0), 1.0 / 3.0);
  s = pow(max(s, 0.0), 1.0 / 3.0);
  return vec3(
    0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s);
}
vec3 oklabToLin(vec3 c) {
  float l = c.x + 0.3963377774 * c.y + 0.2158037573 * c.z;
  float m = c.x - 0.1055613458 * c.y - 0.0638541728 * c.z;
  float s = c.x - 0.0894841775 * c.y - 1.2914855480 * c.z;
  l = l * l * l; m = m * m * m; s = s * s * s;
  return vec3(
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s);
}
vec3 mixColour(vec3 a, vec3 b, float t) {
  if (u_oklab > 0.5) {
    vec3 la = linToOklab(srgbToLinear(a));
    vec3 lb = linToOklab(srgbToLinear(b));
    return clamp(linearToSrgb(oklabToLin(mix(la, lb, t))), 0.0, 1.0);
  }
  return mix(a, b, t);
}

// Przejście przez kolory palety. WebGL1 zabrania dynamicznego indeksowania
// uniformów w shaderze fragmentu, stąd pętla po stałej.
vec3 palette(float x) {
  float n = max(u_colorCount - 1.0, 1.0);
  float f = clamp(x, 0.0, 1.0) * n;
  vec3 col = u_colors[0];
  for (int i = 0; i < 7; i++) {
    if (float(i) < n)
      col = mixColour(col, u_colors[i + 1],
        smoothstep(0.0, 1.0, clamp(f - float(i), 0.0, 1.0)));
  }
  return col;
}

vec3 hueRotate(vec3 col, float a) {
  const mat3 toYIQ = mat3(0.299, 0.596, 0.211,
                          0.587, -0.274, -0.523,
                          0.114, -0.322, 0.312);
  const mat3 toRGB = mat3(1.0, 1.0, 1.0,
                          0.956, -0.272, -1.106,
                          0.621, -0.647, 1.703);
  vec3 yiq = toYIQ * col;
  float ca = cos(a), sa = sin(a);
  yiq = vec3(yiq.x, yiq.y * ca - yiq.z * sa, yiq.y * sa + yiq.z * ca);
  return toRGB * yiq;
}
`;

  /* main(): transformacja pola, kursor, wywołanie shade() i korekcja obrazu. */
  var MAIN = `
void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;
  vec2 screenUv = uv;
  vec2 p = (gl_FragCoord.xy - 0.5 * u_resolution.xy)
    / min(u_resolution.x, u_resolution.y);
  float cursorMask = 0.0;

  // Tryby kursora 1–3 to lokalne zniekształcenia. Push przesuwa te same
  // współrzędne ekranowe przed transformacją pola, żeby zoom i obrót nie
  // zmieniały jego charakteru.
  if (u_cursorPresence > 0.001) {
    vec2 cursor = (0.5 * u_mouse * u_resolution.xy)
      / min(u_resolution.x, u_resolution.y);
    vec2 cursorDelta = p - cursor;
    if (u_cursorEffect < 0.5) {
      p += cursor * u_cursorPresence * u_cursorStrength * 0.55;
    } else {
      float cursorDistance = length(cursorDelta);
      vec2 cursorDirection = cursorDelta / max(cursorDistance, 0.0001);
      cursorMask = u_cursorPresence
        * (1.0 - smoothstep(0.0, u_cursorRadius, cursorDistance));
      if (u_cursorEffect < 1.5) {
        p -= cursorDirection * cursorMask * u_cursorStrength * 0.24;
      } else if (u_cursorEffect < 2.5) {
        float cursorAngle = cursorMask * u_cursorStrength * 2.2;
        float cc = cos(cursorAngle), cs = sin(cursorAngle);
        p = cursor + mat2(cc, -cs, cs, cc) * cursorDelta;
      } else if (u_cursorEffect < 3.5) {
        float ripple = sin(
          cursorDistance / max(u_cursorRadius, 0.001) * 18.0 - u_time * 5.0);
        p -= cursorDirection * ripple * cursorMask * u_cursorStrength * 0.07;
      }
    }
  }

  uv = p * min(u_resolution.x, u_resolution.y) / u_resolution.xy + 0.5;
  p *= u_scale;
  if (abs(u_rotate) > 0.0001) {
    float cr = cos(u_rotate), sr = sin(u_rotate);
    p = mat2(cr, -sr, sr, cr) * p;
  }
  p += u_offset;
  if (u_drift > 0.0001)
    p += u_drift * vec2(sin(u_time * 0.31), cos(u_time * 0.23));
  if (u_warp > 0.0) {
    p += u_warp * (vec2(
      fbm(p * u_detail + u_seed),
      fbm(p * u_detail + vec2(5.2, 1.3))) - 0.5);
  }

  vec3 col;
  if (u_blur > 0.0) {
    float e = u_blur;
    float pe = e * u_scale;
    vec2 uvE = vec2(e) * min(u_resolution.x, u_resolution.y) / u_resolution.xy;
    col  = shade(uv, p, u_time) * 0.36;
    col += shade(uv + vec2(uvE.x, 0.0), p + vec2(pe, 0.0), u_time) * 0.16;
    col += shade(uv - vec2(uvE.x, 0.0), p - vec2(pe, 0.0), u_time) * 0.16;
    col += shade(uv + vec2(0.0, uvE.y), p + vec2(0.0, pe), u_time) * 0.16;
    col += shade(uv - vec2(0.0, uvE.y), p - vec2(0.0, pe), u_time) * 0.16;
  } else {
    col = shade(uv, p, u_time);
  }

  if (abs(u_contrast - 1.0) > 0.0001)
    col = (col - 0.5) * u_contrast + 0.5;
  if (abs(u_saturation - 1.0) > 0.0001) {
    float luma = dot(col, vec3(0.299, 0.587, 0.114));
    col = mix(vec3(luma), col, u_saturation);
  }
  if (abs(u_hue) > 0.0001)
    col = hueRotate(col, u_hue);
  if (abs(u_brightness) > 0.0001)
    col += u_brightness;
  if (u_vignette > 0.0001) {
    float vd = length(screenUv - 0.5) * 1.41421356;
    col *= 1.0 - u_vignette * smoothstep(0.35, 1.0, vd);
  }
  if (u_cursorPresence > 0.001 && u_cursorEffect > 3.5)
    col += (vec3(0.18) + col * 0.12) * cursorMask * u_cursorStrength;
  if (u_grain > 0.0001)
    col += (grainHash(
      gl_FragCoord.xy + vec2(u_seed * 17.0, u_seed * 31.0)) - 0.5) * u_grain;
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
}`;

  /*
   * Wzory. W całym systemie wzór to JEDNA funkcja shade(uv, p, t) -> vec3.
   * Paleta, zawirowanie, transformacja kadru, korekcja i kursor są wspólne.
   * Dostępne wewnątrz: palette(x), fbm(p), noise(p), hash21(p) oraz uniformy
   * u_intensity, u_paramA, u_detail, u_seed, u_scale.
   */
  var PATTERNS = {
    'liquid-chrome': `  // Dwa przebiegi domain warpingu dają „lepkość" cieczy.
  vec2 q = p;
  q += 0.55 * vec2(fbm(q * 1.1 + t * 0.07), fbm(q * 1.1 - t * 0.05));
  q += 0.30 * vec2(fbm(q * 2.4 - t * 0.04), fbm(q * 2.4 + t * 0.06));

  float field = fbm(q * 1.4);

  // Funkcja okresowa zamiast pojedynczego progu: jeden smoothstep dawałby
  // jedną granicę i kadr czytałby się jak dym. Sinus po polu tworzy wiele
  // równoległych pasm — stąd wstęgi.
  float bands = 3.0 + u_intensity * 6.0;
  float w = sin(field * bands * 6.2831853);

  // paramA steruje twardością krawędzi, ale zakres jest ważniejszy, niż się
  // wydaje. Wąskie okno smoothstepa zamienia wstęgi w płaskie kleksy —
  // wychodzi z tego moro, nie metal. Dopiero okno rzędu pełnej amplitudy
  // sinusa (soft ≈ 1) zostawia ciągłą rampę: to ona daje połysk.
  float soft = mix(1.0, 0.05, u_paramA);
  float m = smoothstep(-soft, soft, w);

  // Delikatny połysk na grzbietach pasm: podbija wrażenie metalu.
  m += pow(1.0 - abs(w), 8.0) * 0.12;

  return palette(clamp(m, 0.0, 1.0));`,

    smoke: `  // Trzy warstwy fbm przesunięte w czasie dają wrażenie objętości.
  vec2 q = p * (0.7 + u_intensity);
  float a = fbm(q + vec2(0.0, t * 0.10));
  float b = fbm(q * 1.9 + vec2(a * 1.6, -t * 0.07));
  float c = fbm(q * 3.4 + vec2(-b * 1.2, t * 0.05));
  float d = a * 0.55 + b * 0.30 + c * 0.15;
  // Podbicie kontrastu w środku zakresu zbija mgłę w wyraźne kłęby.
  d = smoothstep(0.25, 0.75 - u_paramA * 0.2, d);
  return palette(d);`,

    plasma: `  float k = 2.0 + u_intensity * 6.0;
  float v = sin(p.x * k + t) + sin(p.y * k * 0.8 - t * 0.7)
    + sin((p.x + p.y) * k * 0.6 + t * 0.5)
    + sin(length(p) * k * 1.2 - t);
  return palette(0.5 + 0.5 * sin(v + u_seed));`,

    aurora: `  // Pionowe pasma modulowane szumem — ruch głównie w osi Y.
  float drift = t * 0.12;
  float band = fbm(vec2(p.x * (0.8 + u_intensity * 2.0), p.y * 0.35 - drift));
  float veil = fbm(vec2(p.x * 2.7 + 4.0, p.y * 0.9 - drift * 1.7));
  float f = band * 0.75 + veil * 0.25;
  // Wygaszanie ku dołowi kadru — zorza wisi u góry.
  f *= smoothstep(-1.2, 0.9, -p.y + u_paramA);
  return palette(f);`,

    'aurora-curtain': `  // Kurtyna. Różni się od zorzy konstrukcją, nie kolorem: tam dwa fbm-y
  // są mieszane, tutaj pierwszy jest WSPÓŁRZĘDNĄ drugiego. Pasma przestają
  // być równoległe i fałdują się jedno w drugie jak tkanina.
  float curtain = fbm(vec2(p.x * 2.0 + t * 0.15, p.y * 0.6 - t * 0.05) + u_seed);
  float band = fbm(vec2(p.x * 3.5 - t * 0.1, curtain * (2.0 + u_intensity * 3.0)));
  float glow = smoothstep(0.15, 0.85, band) * (1.0 - abs(p.y) * 0.7);
  return palette(clamp(glow, 0.0, 1.0));`,

    mesh: `  // Cztery ogniska krążące po elipsach; kolor bierze się z odległości
  // do najbliższego z nich, wygładzonej wykładniczo.
  float sp = 0.35 + u_intensity * 0.5;
  vec2 c1 = vec2(sin(t * sp * 0.7), cos(t * sp * 0.9)) * 0.7;
  vec2 c2 = vec2(cos(t * sp * 0.5 + 1.7), sin(t * sp * 0.6 + 0.4)) * 0.8;
  vec2 c3 = vec2(sin(t * sp * 0.4 + 3.1), cos(t * sp * 0.8 + 2.2)) * 0.6;
  vec2 c4 = vec2(cos(t * sp * 0.9 + 0.8), sin(t * sp * 0.3 + 1.1)) * 0.75;
  float w = 0.35 + u_paramA * 0.6;
  float e = exp(-length(p - c1) / w) + exp(-length(p - c2) / w)
          + exp(-length(p - c3) / w) + exp(-length(p - c4) / w);
  return palette(clamp(e * 0.42, 0.0, 1.0));`,
  };

  /* Wspólna podstawa konfiguracji — preset nadpisuje tylko to, co go różni. */
  var BASE = {
    oklab: 1, scale: 1.5, intensity: 0.48, paramA: 0.5, warp: 0.9,
    detail: 2.4, contrast: 1, brightness: 0, saturation: 1,
    hue: 0, vignette: 0.35, blur: 0, grain: 0.14,
    seed: 7, rotate: 0, offsetX: 0, offsetY: 0, drift: 0.15, timeScale: 0.6,
    cursorEnabled: true, cursorEffect: 0, cursorStrength: 0.4, cursorRadius: 0.3,
  };

  /*
   * Presety. Każdy ma `tone` — deklarację, czy kadr jest jasny, czy ciemny.
   * Renderer i tak mierzy luminancję, ale pierwszy pomiar przychodzi po
   * kilkuset milisekundach; bez deklaracji jasne tło miałoby przez tę chwilę
   * kremowy tekst na kremowym tle. `dark` znaczy „tekst ma być jasny".
   *
   * Presety spoza palety marki (neon) są tu świadomie: Brand Book v3 (s. 08)
   * dopuszcza je wyłącznie kampanijnie, nigdy na stronie produktu. W schemacie
   * sekcji są opisane jako kampanijne.
   */
  var PRESETS = {
    'chrome-noc': {
      tone: 'dark', pattern: 'liquid-chrome',
      // Pięć stopni, nie trzy: rampa jest ciągła, więc każdy dodatkowy stopień
      // to realny stopień połysku. Dwa najciemniejsze siedzą blisko siebie —
      // dolna połowa zakresu zostaje prawie czarna i kadr trzyma się ciemno
      // mimo jasnych grzbietów wstęg.
      colors: ['#0D0D0D', '#131312', '#2C2A26', '#5A564C', '#C9C3B6'],
      scale: 1.25, intensity: 0.5, paramA: 0.08, warp: 0.55, detail: 1.8,
      contrast: 1.08, vignette: 0.5, grain: 0.07, timeScale: 0.4,
    },
    piasek: {
      tone: 'dark', pattern: 'smoke',
      colors: ['#0D0D0D', '#2A2620', '#6B6860', '#C8A875'],
      intensity: 0.6, paramA: 0.35, vignette: 0.5, timeScale: 0.35,
    },
    'plazma-noc': {
      tone: 'dark', pattern: 'plasma',
      colors: ['#0D0D0D', '#242018', '#8A7F6E', '#F2EFE6'],
      warp: 0, contrast: 0.92, brightness: -0.35, vignette: 0.55,
      grain: 0.25, timeScale: 0.7,
    },
    zorza: {
      tone: 'dark', pattern: 'aurora',
      colors: ['#0D0D0D', '#123B34', '#4FB3A0', '#EAF6F2'],
      intensity: 0.35, paramA: 0.3, warp: 0.5, vignette: 0.4, timeScale: 0.5,
    },
    // Uwaga dla edytujących: paleta jest fioletowo-różowa, a kadr wychodzi
    // zielono-turkusowy. To nie pomyłka — robi to hue 2.48 (142°). Zanim
    // ruszysz hexy, wyzeruj hue, żeby zobaczyć paletę taką, jaka jest.
    kurtyna: {
      tone: 'dark', pattern: 'aurora-curtain',
      colors: ['#0A0A12', '#5B2A86', '#D64FA8', '#FFC2E2'],
      // Ta wklejka miesza kolory w sRGB, nie w OKLab. Zostawiamy jak przyszło:
      // OKLab wypłaszcza przejście fiolet → róż, a to ono niesie cały efekt.
      oklab: 0, scale: 1.76, intensity: 0.61, paramA: 0.52, warp: 0.02,
      detail: 2.02, contrast: 0.97, brightness: -0.07, saturation: 1.18,
      hue: 2.48, vignette: 0.29, blur: 0.01, grain: 0.09, seed: 3549,
      rotate: 3.96, offsetX: -0.07, offsetY: 0.21, drift: 0.05, timeScale: 1.01,
      cursorEffect: 3, cursorStrength: 0.44, cursorRadius: 0.69,
    },
    'siatka-jasna': {
      tone: 'light', pattern: 'mesh',
      colors: ['#F2EFE6', '#E4DED0', '#C8A875', '#B8B2A7'],
      intensity: 0.4, paramA: 0.55, warp: 0.3, vignette: 0.18, grain: 0.1,
      timeScale: 0.4,
    },
  };

  /* #RRGGBB → [r,g,b] w zakresie 0..1. Nieprawidłowy hex nie może dać NaN:
     NaN w uniformie daje czarny ekran bez żadnego komunikatu. */
  function hexToRgb(hex) {
    var h = String(hex).trim().replace(/^#/, '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return [0, 0, 0];
    var n = parseInt(h, 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }

  /** Pełna konfiguracja presetu: baza + różnice + paleta dopełniona do ośmiu
      pozycji, bo tyle oczekuje uniform u_colors. */
  function resolvePreset(id) {
    var preset = PRESETS[id] || PRESETS['chrome-noc'];
    var config = {};
    var key;
    for (key in BASE) config[key] = BASE[key];
    for (key in preset) if (key !== 'colors' && key !== 'tone') config[key] = preset[key];

    var rgb = preset.colors.map(hexToRgb);
    var last = rgb[rgb.length - 1] || [0, 0, 0];
    config.colors = [];
    for (var i = 0; i < 8; i++) config.colors.push(rgb[i] || last);
    config.colorCount = rgb.length;
    config.tone = preset.tone;
    return config;
  }

  /** Luminancja względna wg WCAG. */
  function relLuminance(r, g, b) {
    function f(c) {
      var s = c / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    }
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
  }

  /*
   * Zjazd z rozdzielczością na słabym sprzęcie.
   *
   * Te shadery liczą po kilka oktaw szumu na piksel. Na karcie graficznej to
   * nic; bez akceleracji sprzętowej strona potrafi zejść do dwóch klatek na
   * sekundę i przestać reagować. Mierzymy realny odstęp między klatkami
   * i przy trwałym spadku tniemy rozdzielczość renderu. Tło jest miękkie
   * i ziarniste — nikt nie zauważy, że liczy się je w połowie pikseli.
   * Zamrożonej strony zauważy każdy.
   *
   * Tylko w dół: podnoszenie z powrotem dałoby oscylację, bo niższa
   * rozdzielczość natychmiast poprawia pomiar.
   */
  var RENDER_SCALES = [1, 0.6, 0.35, 0.2];

  if (!window.customElements || customElements.get('aura-hero-shader')) return;

  var AuraHeroShader = function () {
    return Reflect.construct(HTMLElement, [], AuraHeroShader);
  };
  AuraHeroShader.prototype = Object.create(HTMLElement.prototype);
  AuraHeroShader.prototype.constructor = AuraHeroShader;
  Object.setPrototypeOf(AuraHeroShader, HTMLElement);

  /*
   * Cała inicjalizacja WebGL czeka na wolny wątek.
   *
   * Kompilacja tego fragment shadera (kilka oktaw szumu, domain warping,
   * OKLab) plus pierwsza klatka kosztowały w audycie PageSpeed 1706 ms na
   * emulowanym Moto G Power — najdroższa pojedyncza pozycja na stronie,
   * i to mimo że na telefonie rysujemy tylko JEDNĄ klatkę.
   *
   * Ten koszt nie znika, ale nie musi wypadać w oknie, w którym przeglądarka
   * układa stronę, a użytkownik czeka na treść. Pod spodem leży już barwa
   * bazowa presetu z tej samej palety, a wejście shadera jest crossfadem
   * (data-shader='on'), więc odsunięcie go o sekundę wygląda jak zamierzony
   * efekt, a nie jak brakujące tło.
   */
  function whenIdle(fn) {
    function go() {
      if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 3000 });
      else window.setTimeout(fn, 1200);
    }
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  }

  AuraHeroShader.prototype.connectedCallback = function () {
    if (this.started) return;
    this.started = true;

    var host = this;
    var canvas = this.querySelector('canvas');
    var section = this.closest('[data-aura-hero]') || this;
    if (!canvas) return;

    /*
     * TELEFON: wychodzimy PRZED czymkolwiek związanym z WebGL.
     *
     * Odroczenia (whenIdle, jedna klatka, obniżona skala) przesuwały koszt,
     * ale go nie usuwały: sam kontekst, kompilacja fragment shadera
     * i linkowanie programu to wydatek niezależny od tego, ile klatek
     * narysujemy. Pomiar A/B na replice z dławieniem CPU 4x:
     *
     *   shader włączony   TBT 674 ms, 3 długie zadania, najdłuższe 662 ms
     *   shader wyłączony  TBT 0 ms,   0 długich zadań
     *
     * Na telefonie tło daje poster WebP z tego samego renderera (7-11 kB),
     * podpięty w CSS sekcji hero pod tą samą granicą 749 px. Wychodzimy przed
     * getContext, więc nie powstaje ani kontekst, ani kompilacja, ani
     * readPixels — a nie tylko „nie animujemy".
     *
     * Granica jest CELOWO bezwarunkowa, także wobec ustawienia `motion`.
     * Poszanowanie `motion: always` tutaj dałoby stan, w którym CSS ukrywa
     * canvas, a WebGL i tak liczy w tle — czyli pełny koszt bez obrazu.
     * `motion` zachowuje znaczenie na desktopie.
     *
     * Ton tekstu nie ucierpi: sekcja renderuje data-on z tabeli presetów
     * server-side, więc kolor jest poprawny od pierwszego malowania i nie
     * potrzebuje pomiaru luminancji.
     */
    if (window.matchMedia('(max-width: 749px)').matches) {
      canvas.dataset.webgl = 'off';
      section.dataset.shader = 'poster';
      return;
    }

    /*
     * RENDEROWANIE PROGRAMOWE: poster zamiast shadera.
     *
     * PSI desktop pokazał TBT 13 830 ms przy wielosekundowych zadaniach
     * z tego pliku. Bez GPU każda operacja shadera liczy się na CPU:
     * kompilacja, linkowanie i KAŻDA klatka pętli. Dotyczy to nie tylko
     * runnera PSI — realni użytkownicy na maszynach bez akceleracji dostają
     * to samo.
     *
     * SHADER JEST PROGRESSIVE ENHANCEMENT. Wchodzi wyłącznie przy
     * POTWIERDZONYM sprzętowym GPU. Pusta lub nieodczytana nazwa renderera
     * (WEBGL_debug_renderer_info bywa zablokowane przez ustawienia
     * prywatności) też oznacza poster — brak potwierdzenia nie jest
     * potwierdzeniem.
     *
     * Decyduje wyłącznie zdolność sprzętu. Zero wykrywania Lighthouse'a,
     * zero user-agenta.
     *
     * KONTEKST POWSTAJE TUTAJ, NIE W boot(). boot() leci przez whenIdle
     * z timeoutem 3000 ms, więc przy renderowaniu programowym poster
     * zacząłby się pobierać dopiero po bezczynności. Desktopowe LCP wynosi
     * dziś 1,0 s — duży obraz ładowany w trzeciej sekundzie stałby się nowym
     * elementem LCP i wymienilibyśmy TBT na LCP. Kompilacja i pętla zostają
     * odroczone jak dotąd; tutaj pada wyłącznie utworzenie kontekstu
     * i odczyt nazwy renderera.
     */
    var gl = canvas.getContext('webgl', {
      antialias: false,
      // Wymagane, żeby readPixels widziało narysowaną klatkę.
      preserveDrawingBuffer: true,
    });

    /*
     * Canvas po utracie kontekstu jest zużyty i wymaga wymiany.
     *
     * Zmierzone: getContext na canvasie, któremu wcześniej odebrano kontekst,
     * zwraca TEN SAM, stracony obiekt — nie null. Na straconym kontekście
     * WEBGL_debug_renderer_info jest niedostępne, więc nazwa renderera
     * wychodzi pusta i sekcja zostawałaby na posterze NA ZAWSZE.
     *
     * Dotyka to customizera, gdzie sekcja odpina się i wpina przy każdej
     * zmianie suwaka. Świeży element canvas dostaje sprawny kontekst,
     * więc podmieniamy go i pytamy jeszcze raz.
     */
    if (gl && gl.isContextLost()) {
      var fresh = document.createElement('canvas');
      fresh.className = canvas.className;
      fresh.setAttribute('aria-hidden', 'true');
      canvas.parentNode.replaceChild(fresh, canvas);
      canvas = fresh;
      gl = canvas.getContext('webgl', { antialias: false, preserveDrawingBuffer: true });
    }

    // Brak WebGL: CSS chowa canvas, zostaje poster albo barwa bazowa presetu.
    if (!gl || gl.isContextLost()) {
      canvas.dataset.webgl = 'off';
      section.dataset.shader = 'poster';
      return;
    }

    var dbg = gl.getExtension('WEBGL_debug_renderer_info');
    var rendererName = dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || '') : '';
    var software = /swiftshader|llvmpipe|software|basic render|mesa offscreen/i.test(rendererName);

    if (rendererName === '' || software) {
      // Kontekst nie będzie już użyty — zwalniamy go od razu.
      var lose = gl.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
      canvas.dataset.webgl = 'off';
      section.dataset.shader = 'poster';
      return;
    }

    canvas.dataset.webgl = 'on';

    // Jedyne miejsce, z którego boot() bierze kontekst. boot() nie woła
    // getContext — patrz komentarz tam.
    this.__auraGl = gl;

    // Sprzątanie musi istnieć od razu: customizer potrafi wyrzucić sekcję,
    // zanim shader zdąży wystartować.
    // Reset przy każdym podpięciu: customizer odłącza i podpina sekcję przy
    // każdej zmianie suwaka, a flaga z poprzedniego życia blokowałaby start.
    this.__auraCancelled = false;
    var booted = false;

    /*
     * Sprzątanie SPRZED boot(). Odkąd kontekst powstaje tutaj, samo
     * podniesienie flagi nie wystarcza: odłączenie sekcji przed dojściem
     * do boot() zostawiałoby żywy kontekst WebGL. W customizerze, gdzie
     * sekcja odpina się i wpina przy każdym ruchu suwaka, wyczerpałoby to
     * limit kontekstów przeglądarki w kilkanaście zmian.
     *
     * Po starcie boot() podmienia tę funkcję na pełne sprzątanie
     * (bufory, program, listenery, loseContext) — jak dotąd.
     */
    this.dispose = function () {
      host.__auraCancelled = true;
      if (host.__auraGl) {
        var l = host.__auraGl.getExtension('WEBGL_lose_context');
        if (l) l.loseContext();
        host.__auraGl = null;
      }
    };

    whenIdle(function () {
      if (host.__auraCancelled || booted) return;
      booted = true;
      host.boot();
    });
  };

  AuraHeroShader.prototype.boot = function () {
    var host = this;
    var canvas = this.querySelector('canvas');
    var section = this.closest('[data-aura-hero]') || this;
    if (!canvas) return;

    var presetId = this.dataset.preset || 'chrome-noc';
    var cfg = resolvePreset(presetId);

    // Ustawienia sekcji nadpisują preset — merchant kręci tempem i ziarnem
    // bez wchodzenia w kod.
    if (this.dataset.timeScale) cfg.timeScale = parseFloat(this.dataset.timeScale);
    if (this.dataset.intensity) cfg.intensity = parseFloat(this.dataset.intensity);
    if (this.dataset.cursor === 'false') cfg.cursorEnabled = false;

    /*
     * Kontekst powstał już w connectedCallback, razem ze sprawdzeniem, czy
     * pod spodem jest sprzętowy GPU. boot() go tylko przejmuje i NIGDY nie
     * woła getContext ponownie — drugie wywołanie stworzyłoby drugi kontekst
     * na każdej maszynie ze sprzętem.
     *
     * Jeśli go nie ma, znaczy że connectedCallback wyszedł wcześniej (brak
     * WebGL, renderowanie programowe, nieodczytana nazwa renderera albo
     * telefon) i zdecydował już o posterze. Nie ma tu nic do zrobienia.
     */
    var gl = this.__auraGl;
    if (!gl) return;

    function compile(type, src) {
      var s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.error('[aura-shader]', gl.getShaderInfoLog(s));
      }
      return s;
    }

    var program = gl.createProgram();
    var shade = PATTERNS[cfg.pattern] || PATTERNS['liquid-chrome'];
    var vs = compile(gl.VERTEX_SHADER, VERT);
    var fs = compile(gl.FRAGMENT_SHADER, PREAMBLE + '\nvec3 shade(vec2 uv, vec2 p, float t) {\n' + shade + '\n}\n' + MAIN);
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    gl.useProgram(program);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var uni = {
      colors: gl.getUniformLocation(program, 'u_colors'),
      scene: gl.getUniformLocation(program, 'u_scene'),
      shape: gl.getUniformLocation(program, 'u_shape'),
      surface: gl.getUniformLocation(program, 'u_surface'),
      finish: gl.getUniformLocation(program, 'u_finish'),
      transform: gl.getUniformLocation(program, 'u_transform'),
      space: gl.getUniformLocation(program, 'u_space'),
      cursor: gl.getUniformLocation(program, 'u_cursor'),
    };

    var flat = [];
    cfg.colors.forEach(function (c) { flat.push(c[0], c[1], c[2]); });
    gl.uniform3fv(uni.colors, new Float32Array(flat));
    gl.uniform4f(uni.shape, cfg.scale, cfg.intensity, cfg.paramA, cfg.warp);
    gl.uniform4f(uni.surface, cfg.detail, cfg.contrast, cfg.brightness, cfg.saturation);
    gl.uniform4f(uni.finish, cfg.hue, cfg.vignette, cfg.blur, cfg.grain);
    gl.uniform4f(uni.transform, cfg.seed, cfg.rotate, cfg.drift, cfg.oklab);

    var targetX = 0, targetY = 0, targetPresence = 0;
    var mouseX = 0, mouseY = 0, cursorPresence = 0;
    var pointerKnown = false, pointerClientX = 0, pointerClientY = 0;
    var bounds = canvas.getBoundingClientRect();
    var raf = 0, lastNow = null;
    var visible = document.visibilityState === 'visible';
    var inView = true, disposed = false;
    var lastSample = 0, lastTone = null, lastFlip = 0, smoothLum = null;
    var drawnOnce = false;
    var start = performance.now();

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Przy ograniczonym ruchu zamrażamy obraz na reprezentatywnej klatce —
    // tło nie znika, po prostu przestaje się ruszać.
    var frozenTime = 12;

    /*
     * KIEDY W OGÓLE ANIMUJEMY.
     *
     * Ten shader liczy kilka oktaw szumu na piksel. Na pomiarze z dławieniem
     * CPU 4x (tak liczy PageSpeed) pojedyncza klatka w pełnej rozdzielczości
     * zajmowała 1,4 s, a osiem sekund animacji dawało 4,4 s zablokowanego
     * wątku głównego. To jest wprost przyczyna Total Blocking Time w audycie
     * i jankowatego scrolla na słabszym telefonie.
     *
     * Dlatego domyślnie: JEDNA klatka od razu (tło wygląda jak ma wyglądać,
     * LCP dostaje prawdziwą treść), a ruch dopiero wtedy, gdy strona skończy
     * się ładować i wątek jest wolny. Na małych ekranach i słabych maszynach
     * zostaje sama klatka — obraz jest ten sam, po prostu nie płynie.
     */
    var motion = host.dataset.motion || 'auto';
    var smallScreen = window.matchMedia('(max-width: 749px)').matches;
    // Próg celowo niski. hardwareConcurrency to słaby wskaźnik mocy — cztery
    // rdzenie ma i stary telefon, i sprawny laptop. Odsiewamy więc tylko
    // maszyny skrajnie słabe, a o reszcie decyduje szerokość ekranu.
    var lowCores = (navigator.hardwareConcurrency || 8) <= 2;
    var conn = navigator.connection;
    var saveData = !!(conn && conn.saveData);

    var staticOnly = reduce || motion === 'never';
    if (!staticOnly && motion !== 'always') {
      staticOnly = smallScreen || lowCores || saveData;
    }

    // Animacja rusza dopiero po oddaniu sterowania — nigdy w oknie, w którym
    // przeglądarka układa i maluje stronę.
    var animate = false;

    /*
     * Rozdzielczość startowa dobierana z góry, nie po fakcie.
     *
     * Zjazd niżej (considerDownscale) jest reaktywny: potrzebuje 600 ms
     * rozgrzewki i 20 wolnych klatek. Przez ten czas maszyna, która sobie nie
     * radzi, i tak zdąży zaciąć stronę. Taniej jest zacząć nisko i ewentualnie
     * nigdy nie wchodzić wyżej — na miękkim, ziarnistym tle nikt nie zobaczy
     * różnicy, a zawieszoną stronę zobaczy każdy.
     */
    var initialStep = 0;
    if (smallScreen || lowCores) initialStep = 2;
    else if ((window.devicePixelRatio || 1) > 1.5) initialStep = 1;

    var scaleStep = initialStep, slowFrames = 0, warmUntil = null, frameEma = null;

    function considerDownscale(dtMs) {
      if (scaleStep >= RENDER_SCALES.length - 1) return;
      // Próg „to była pauza" wysoko: przy jednej klatce na pół sekundy — czyli
      // dokładnie w przypadku, dla którego to powstało — niższy próg
      // odsiewałby te klatki jako wznowienie i mechanizm nigdy by nie zadziałał.
      if (dtMs > 2000) return;

      // Rozgrzewka liczona w czasie, nie w klatkach: „pierwsze dziesięć klatek"
      // na maszynie robiącej dwie klatki na sekundę to pięć sekund zamrożonej
      // strony, czyli dokładnie to, czego unikamy.
      if (warmUntil === null) warmUntil = performance.now() + 600;
      if (performance.now() < warmUntil) return;

      frameEma = frameEma === null ? dtMs : frameEma + (dtMs - frameEma) * 0.2;

      // Liczy się i średnia, i bieżąca klatka. Sama średnia pamięta wolny start
      // długo po tym, jak maszyna weszła w rytm.
      slowFrames = dtMs > 40 && frameEma > 40
        ? slowFrames + Math.ceil(frameEma / 40)
        : Math.max(0, slowFrames - 2);
      if (slowFrames < 20) return;

      scaleStep++;
      slowFrames = 0;
      warmUntil = null;
      frameEma = null;
      resizeCanvas();
    }

    function resizeCanvas() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var rawW = Math.max(1, Math.round(bounds.width * dpr));
      var rawH = Math.max(1, Math.round(bounds.height * dpr));
      var budget = Math.min(1, Math.sqrt(2000000 / Math.max(1, rawW * rawH)));
      var scale = budget * RENDER_SCALES[scaleStep];
      var w = Math.max(1, Math.round(rawW * scale));
      var h = Math.max(1, Math.round(rawH * scale));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    }

    function requestRender() {
      if (!disposed && visible && inView && raf === 0) raf = requestAnimationFrame(render);
    }

    function updatePointerTarget() {
      if (!pointerKnown || bounds.width === 0 || bounds.height === 0) return;
      var inside = pointerClientX >= bounds.left && pointerClientX <= bounds.right &&
        pointerClientY >= bounds.top && pointerClientY <= bounds.bottom;
      if (!inside) { targetPresence = 0; requestRender(); return; }
      var nx = ((pointerClientX - bounds.left) / bounds.width) * 2 - 1;
      var ny = -(((pointerClientY - bounds.top) / bounds.height) * 2 - 1);
      if (targetPresence === 0 && cursorPresence < 0.01) { mouseX = nx; mouseY = ny; }
      targetX = nx; targetY = ny; targetPresence = 1;
      requestRender();
    }

    /*
     * getBoundingClientRect() wymusza przeliczenie układu strony. Wołane
     * z każdego zdarzenia pointermove i scroll dawało „Wymuszone
     * przeformatowanie" w audycie i szarpanie przy przewijaniu. Odczyt
     * spinamy więc z klatką: raz na rAF, nigdy raz na zdarzenie.
     */
    var boundsDirty = false, boundsRaf = 0;

    function refreshBounds() {
      boundsRaf = 0;
      if (disposed || !boundsDirty) return;
      boundsDirty = false;
      bounds = canvas.getBoundingClientRect();
      updatePointerTarget();
    }

    function markBoundsDirty() {
      boundsDirty = true;
      if (boundsRaf === 0) boundsRaf = requestAnimationFrame(refreshBounds);
    }

    function onPointerMove(e) {
      pointerKnown = true;
      pointerClientX = e.clientX;
      pointerClientY = e.clientY;
      markBoundsDirty();
    }
    function onPointerLeave() { pointerKnown = false; targetPresence = 0; requestRender(); }
    function updateLayout() {
      bounds = canvas.getBoundingClientRect();
      resizeCanvas();
      updatePointerTarget();
      requestRender();
    }

    window.addEventListener('resize', updateLayout);
    var cursorOn = cfg.cursorEnabled && !reduce;
    if (cursorOn) {
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('pointercancel', onPointerLeave);
      window.addEventListener('scroll', markBoundsDirty, { passive: true, capture: true });
      window.addEventListener('blur', onPointerLeave);
      document.documentElement.addEventListener('pointerleave', onPointerLeave);
    }

    var resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(canvas);

    var intersectionObserver = new IntersectionObserver(function (entries) {
      inView = entries[0] ? entries[0].isIntersecting : true;
      if (inView) requestRender();
      else if (raf !== 0) { cancelAnimationFrame(raf); raf = 0; lastNow = null; }
    });
    intersectionObserver.observe(canvas);

    function onVisibilityChange() {
      visible = document.visibilityState === 'visible';
      if (visible) requestRender();
      else if (raf !== 0) { cancelAnimationFrame(raf); raf = 0; lastNow = null; }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    /*
     * Start animacji po oddaniu sterowania.
     *
     * Kolejność: pierwsza klatka leci od razu (requestRender niżej), potem
     * czekamy na koniec ładowania i na wolną chwilę wątku. Dzięki temu ruch
     * nie wchodzi w okno, w którym liczy się Total Blocking Time i Speed
     * Index, a użytkownik i tak widzi właściwe tło od początku.
     */
    /*
     * Ruch startuje klatkę po pierwszym rysunku, nie od razu. boot() już leci
     * po bezczynności, więc wystarczy oddać wątek raz, żeby kompilacja
     * i pierwsza klatka rozliczyły się osobno od pętli animacji.
     */
    var motionTimer = 0;
    if (!staticOnly) {
      motionTimer = window.setTimeout(function () {
        if (disposed) return;
        animate = true;
        lastNow = null;
        requestRender();
      }, 0);
    }

    /*
     * Automatyczny kontrast tekstu.
     *
     * Tło może mieć dowolny kolor, więc paleta marki nie gwarantuje
     * czytelności. Co ok. 400 ms czytamy jasność wycinka pod nagłówkiem
     * i przestawiamy data-on na sekcji; CSS podmienia kolory tekstu
     * i przesłony. readPixels wymusza synchronizację GPU→CPU, więc robimy to
     * rzadko i na maleńkim wycinku (maks. 32×16 px).
     *
     * Czytamy sam shader, bez przesłony — i tak ma być. Przesłona jest
     * NASTĘPSTWEM tonu, nie jego przyczyną; domieszanie jej tutaj zapętliłoby
     * decyzję.
     */
    var SAMPLE = { x: 0.05, y: 0.55, w: 0.5, h: 0.35 };

    function sampleLuminance() {
      var sx = Math.max(0, Math.floor(canvas.width * SAMPLE.x));
      var sy = Math.max(0, Math.floor(canvas.height * (1 - SAMPLE.y - SAMPLE.h)));
      var sw = Math.max(1, Math.min(32, Math.floor(canvas.width * SAMPLE.w)));
      var sh = Math.max(1, Math.min(16, Math.floor(canvas.height * SAMPLE.h)));
      var px = new Uint8Array(sw * sh * 4);
      gl.readPixels(sx, sy, sw, sh, gl.RGBA, gl.UNSIGNED_BYTE, px);

      var sum = 0;
      for (var i = 0; i < px.length; i += 4) sum += relLuminance(px[i], px[i + 1], px[i + 2]);
      var raw = sum / (px.length / 4);

      // Wygładzanie w czasie. Pojedyncza próbka skacze o pół zakresu, gdy przez
      // obszar tekstu przepłynie jasna smuga — bez tego ton przeskakiwał
      // w kółko, a razem z nim kolor tekstu. Widać to było jako miganie.
      smoothLum = smoothLum === null ? raw : smoothLum + (raw - smoothLum) * 0.25;
      var lum = smoothLum;

      // Szeroka histereza (0,38 / 0,62) zamiast progu w połowie.
      var tone = lastTone === 'light'
        ? (lum < 0.38 ? 'dark' : 'light')
        : (lum > 0.62 ? 'light' : 'dark');

      if (tone === lastTone) return;

      // Minimum 4 s między przełączeniami. Nawet przy wygładzaniu tło o dużej
      // amplitudzie potrafi przejść próg co kilka sekund; częstsza zmiana
      // koloru tekstu rozprasza, zamiast pomagać.
      var now = performance.now();
      if (lastTone !== null && now - lastFlip < 4000) return;

      lastFlip = now;
      lastTone = tone;
      section.dataset.on = tone;
    }

    function render(now) {
      raf = 0;
      if (disposed || !visible || !inView) return;

      var rawDt = lastNow === null ? 0 : now - lastNow;

      // Limit ~30 kl./s. Ruch jest wolny i miękki, więc różnicy nie widać,
      // a rachunek za klatki spada o połowę. Wychodzimy przed rysowaniem,
      // żeby ta klatka nic nie kosztowała.
      if (drawnOnce && animate && rawDt > 0 && rawDt < 30) {
        requestRender();
        return;
      }

      var dt = lastNow === null ? 0 : Math.min(rawDt / 1000, 0.1);
      if (rawDt > 0) considerDownscale(rawDt);
      lastNow = now;

      var follow = 1 - Math.exp(-12 * dt);
      mouseX += (targetX - mouseX) * follow;
      mouseY += (targetY - mouseY) * follow;
      cursorPresence += (targetPresence - cursorPresence) * follow;

      resizeCanvas();
      var time = reduce ? frozenTime : ((now - start) / 1000) * cfg.timeScale;
      gl.uniform4f(uni.scene, canvas.width, canvas.height, time, cfg.colorCount);
      gl.uniform4f(uni.space, cfg.offsetX, cfg.offsetY, mouseX, mouseY);
      gl.uniform4f(uni.cursor, cursorOn ? cursorPresence : 0,
        cfg.cursorEffect, cfg.cursorStrength, cfg.cursorRadius);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      if (!drawnOnce) {
        drawnOnce = true;
        // Crossfade z barwy bazowej presetu: pierwsza klatka nie pojawia się
        // skokiem, bo pod spodem leży już kolor z tej samej palety.
        section.dataset.shader = 'on';
      }

      // readPixels wymusza synchronizację GPU→CPU, czyli zatrzymuje wątek.
      // Raz po pierwszej klatce — żeby kolor tekstu był poprawny także wtedy,
      // gdy nic się nie animuje — a potem rzadko.
      if (lastSample === 0 || (animate && now - lastSample > 2000)) {
        lastSample = now;
        sampleLuminance();
      }

      if (!animate) return; // jedna klatka wystarczy

      var settling = Math.abs(targetX - mouseX) > 0.001 ||
        Math.abs(targetY - mouseY) > 0.001 ||
        Math.abs(targetPresence - cursorPresence) > 0.001;
      if (Math.abs(cfg.timeScale) > 0.0001 || settling) requestRender();
      else lastNow = null;
    }

    // Podgląd w customizerze przeładowuje sekcję bez przeładowania strony —
    // bez sprzątania zostawialibyśmy działającą pętlę i kontekst WebGL na
    // każdą zmianę suwaka.
    this.dispose = function () {
      disposed = true;
      host.__auraCancelled = true;
      cancelAnimationFrame(raf);
      if (motionTimer) window.clearTimeout(motionTimer);
      if (boundsRaf) cancelAnimationFrame(boundsRaf);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('resize', updateLayout);
      if (cursorOn) {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointercancel', onPointerLeave);
        window.removeEventListener('scroll', markBoundsDirty, true);
        window.removeEventListener('blur', onPointerLeave);
        document.documentElement.removeEventListener('pointerleave', onPointerLeave);
      }
      gl.deleteBuffer(buf);
      gl.deleteProgram(program);
      var lose = gl.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
    };

    requestRender();
    host.dataset.ready = 'true';
  };

  AuraHeroShader.prototype.disconnectedCallback = function () {
    if (this.dispose) this.dispose();
    this.started = false;
  };

  customElements.define('aura-hero-shader', AuraHeroShader);
})();
